<script setup>
import { useLayout } from '@/layout/composables/layout';
import { onBeforeMount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { stores } from '@/util/stores.js';

const route = useRoute();
const { layoutState, setActiveMenuItem, onMenuToggle } = useLayout();

const props = defineProps({
	item: {
		type: Object,
		default: () => ({})
	},
	index: {
		type: Number,
		default: 0
	},
	root: {
		type: Boolean,
		default: true
	},
	parentItemKey: {
		type: String,
		default: null
	}
});

const isActiveMenu = ref(false);
const itemKey = ref(null);

onBeforeMount(() => {
	itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index);

	const activeItem = layoutState.activeMenuItem;

	isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(itemKey.value + '-') : false;
	if (hasActiveChild()) isActiveMenu.value = true;
});

/** True when one of this item's children is the current route, so the submenu should be open on arrival. */
function hasActiveChild() {
	return Boolean(props.item.items?.some((child) => checkActiveRoute(child)));
}

watch(
	() => route.path,
	() => {
		if (hasActiveChild()) isActiveMenu.value = true;
	}
);

watch(
	() => layoutState.activeMenuItem,
	(newVal) => {
		isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(itemKey.value + '-');
	}
);

function itemClick(event, item) {
	if (item.disabled) {
		event.preventDefault();
		return;
	}

	if ((item.to || item.url) && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
		onMenuToggle();
	}

	if (item.command) {
		item.command({ originalEvent: event, item: item });
	}

	const foundItemKey = item.items ? (isActiveMenu.value ? props.parentItemKey : itemKey.value) : itemKey.value;

	if (layoutState.activeMenuItem !== foundItemKey) {
		setActiveMenuItem(foundItemKey);
	}
}

function checkActiveRoute(item) {
	if (route.path === item.to) return true;
	// Finance section links carry no group; `/app/admin/finance/home` should light up on `/app/admin/finance/home/group/7`.
	return typeof item.to === 'string' && item.to.startsWith('/app/admin/finance/') && route.path.startsWith(item.to + '/');
}
const hasRole = (requiredRoles = []) => stores.usersStore.hasRole(requiredRoles);

const linkClasses = 'relative flex cursor-pointer select-none items-center rounded-border px-4 py-3 text-color outline-none transition-colors hover:bg-[var(--surface-hover)] focus-visible:ring-2 focus-visible:ring-primary';
</script>

<template>
	<li v-if="item.visible !== false && hasRole(item.roles)">
		<div v-if="root && item.visible !== false" class="my-3 text-xs font-bold uppercase text-color">{{ item.label }}</div>
		<a v-if="(!item.to || item.items) && item.visible !== false && !root" :href="item.url" @click="itemClick($event, item, index)" :class="[linkClasses, item.class, { 'ml-4': !root }]" :target="item.target" tabindex="0">
			<font-awesome-icon :icon="item.icon" class="mr-2"></font-awesome-icon>
			<span>{{ item.label }}</span>
			<font-awesome-icon :icon="['fas', 'caret-down']" class="ml-auto text-xs transition-transform" :class="{ 'rotate-180': isActiveMenu }" v-if="item.items"></font-awesome-icon>
		</a>
		<router-link v-if="item.to && !item.items && item.visible !== false" @click="itemClick($event, item, index)" :class="[linkClasses, item.class, { 'font-bold text-primary': checkActiveRoute(item), 'ml-4': !root }]" tabindex="0" :to="item.to">
			<font-awesome-icon :icon="item.icon" class="mr-2"></font-awesome-icon>
			<span>{{ item.label }}</span>
		</router-link>
		<Transition
			v-if="item.items && item.visible !== false"
			enter-active-class="overflow-hidden transition-[max-height] duration-500 ease-in-out"
			enter-from-class="max-h-0"
			enter-to-class="max-h-[1000px]"
			leave-active-class="overflow-hidden transition-[max-height] duration-300 ease-out"
			leave-from-class="max-h-[1000px]"
			leave-to-class="max-h-0"
		>
			<ul v-show="root ? true : isActiveMenu" class="m-0 list-none overflow-hidden rounded-border p-0" :class="{ 'pl-4': !root }">
				<app-menu-item v-for="(child, i) in item.items" :key="child" :index="i" :item="child" :parentItemKey="itemKey" :root="false"></app-menu-item>
			</ul>
		</Transition>
	</li>
</template>
